import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Stack,
  Alert,
  Paper,
} from "@mui/material";

import { ClearCallLogoTitle } from "../../components/clear-call-logo";
import Copyright from "../../components/copyright";
import MeetingConfirmationComponent from "./components/meeting-confirmation-component";
import { useGetMeetingAttendance } from "../../store";
import { MeetingStatus } from "../../types";

/**
 * Meeting Confirmation Page Component
 * Allows users to confirm or deny their attendance for a meeting
 */
const MeetingConfirmationPage: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const [searchParams] = useSearchParams();

  const getConfirm = (confirm: string | undefined | null) => {
    if (!confirm) {
      return null
    }

    return confirm == "true"
  }

  const [userId] = useState(searchParams.get("userId") || "");
  const [joinCode] = useState(searchParams.get("joinCode") || "");
  const [confirm] = useState(getConfirm(searchParams.get("confirm")));

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: attendanceInfo = null, isLoading } = useGetMeetingAttendance(meetingId || "", userId, joinCode)

  const getError = () => {
    if (!joinCode || !userId || !meetingId || confirm == null) {
      return "Invalid meeting confirmation link"
    }

    if (attendanceInfo?.meetingType === MeetingStatus.Ended) {
      return "The meeting has already ended"
    }

    if (attendanceInfo?.meetingType === MeetingStatus.Cancelled) {
      return "The meeting has been cancelled"
    }

    if (attendanceInfo?.meetingType === MeetingStatus.Abandoned) {
      return "The meeting was abandoned"
    }

    if (attendanceInfo && attendanceInfo.meetingType != MeetingStatus.Scheduled) {
      if (attendanceInfo?.message) {
        return attendanceInfo?.message
      }

      return "Meeting status is " + attendanceInfo?.meetingType
    }

    if (!attendanceInfo && !isLoading) {
      return "Failed to check attendance"
    }

    return errorMessage
  }

  const error = getError()

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(0deg,rgb(222, 238, 253),rgb(172, 220, 226))",
      }}
    >
      <Paper
        elevation={20}
        sx={{
          width: 400,
          p: 4,
          alignItems: "center",
          borderRadius: 2,
        }}
      >
        <Stack
          spacing={3}
          alignItems="center"
          justifyContent="center"
          sx={{
            width: "100%",
            pt: 2,
          }}
        >
          <ClearCallLogoTitle size={150} />
          {error && <Stack spacing={2} sx={{ mt: 2, width: "100%" }}>
            <Alert severity="error">{error}</Alert>
          </Stack>}
          {attendanceInfo && meetingId && !error && (
            attendanceInfo.meetingType === MeetingStatus.Scheduled ? (
            <MeetingConfirmationComponent
              userId={userId}
              meetingId={meetingId}
              joinCode={joinCode}
              confirm={confirm}
              setError={setErrorMessage}
            />
            ) : (
              <Alert severity="error">Meeting status is {attendanceInfo.meetingType}</Alert>
            ))}
        </Stack>
      </Paper>
      <Copyright sx={{ mt: 5 }} />
    </Stack>
  );
};

export default MeetingConfirmationPage;
